import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table } from 'react-bootstrap';
import { FaBox, FaTools, FaHandshake, FaEnvelope } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { productsAPI, servicesAPI, partnersAPI, contactsAPI } from '../services/api';
import * as Icons from 'react-icons/fa';
import Icon from '../components/Icon';

interface DashboardStats {
  products: number;
  services: number;
  partners: number;
  contacts: number;
  recentContacts: any[];
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    products: 0,
    services: 0,
    partners: 0,
    contacts: 0,
    recentContacts: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch counts from all endpoints
        const [productsRes, servicesRes, partnersRes, contactsRes] = await Promise.all([
          productsAPI.getProducts(),
          servicesAPI.getServices(),
          partnersAPI.getPartners(),
          contactsAPI.getContacts(),
        ]);

        setStats({
          products: productsRes.data.length || 0,
          services: servicesRes.data.length || 0,
          partners: partnersRes.data.length || 0,
          contacts: contactsRes.data.length || 0,
          recentContacts: contactsRes.data.slice(0, 5) || [],
        });
      } catch (err: any) {
        setError('Failed to load dashboard data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Container>
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading dashboard data...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <div className="alert alert-danger my-5" role="alert">
          {error}
        </div>
      </Container>
    );
  }

  return (
    <Container fluid>
      <h1 className="mb-4">Dashboard</h1>
      
      {/* Stats Cards */}
      <Row className="g-4 mb-5">
        <Col sm={6} xl={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-primary bg-opacity-10 me-3">
                <Icon icon={Icons.FaBox} className="fs-4 text-primary" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Products</h6>
                <h3 className="fs-4 mb-0">{stats.products}</h3>
              </div>
            </Card.Body>
            <Card.Footer className="border-0 bg-transparent">
              <Link to="/products" className="text-decoration-none">View all products</Link>
            </Card.Footer>
          </Card>
        </Col>
        
        <Col sm={6} xl={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-success bg-opacity-10 me-3">
                <Icon icon={Icons.FaTools} className="fs-4 text-success" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Services</h6>
                <h3 className="fs-4 mb-0">{stats.services}</h3>
              </div>
            </Card.Body>
            <Card.Footer className="border-0 bg-transparent">
              <Link to="/services" className="text-decoration-none">View all services</Link>
            </Card.Footer>
          </Card>
        </Col>
        
        <Col sm={6} xl={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-warning bg-opacity-10 me-3">
                <Icon icon={Icons.FaHandshake} className="fs-4 text-warning" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Partners</h6>
                <h3 className="fs-4 mb-0">{stats.partners}</h3>
              </div>
            </Card.Body>
            <Card.Footer className="border-0 bg-transparent">
              <Link to="/partners" className="text-decoration-none">View all partners</Link>
            </Card.Footer>
          </Card>
        </Col>
        
        <Col sm={6} xl={3}>
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="d-flex align-items-center">
              <div className="rounded-circle p-3 bg-danger bg-opacity-10 me-3">
                <Icon icon={Icons.FaEnvelope} className="fs-4 text-danger" />
              </div>
              <div>
                <h6 className="text-muted mb-1">Contact Requests</h6>
                <h3 className="fs-4 mb-0">{stats.contacts}</h3>
              </div>
            </Card.Body>
            <Card.Footer className="border-0 bg-transparent">
              <Link to="/contacts" className="text-decoration-none">View all contacts</Link>
            </Card.Footer>
          </Card>
        </Col>
      </Row>
      
      {/* Recent Contacts Table */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-white py-3">
          <h5 className="mb-0">Recent Contact Requests</h5>
        </Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover className="mb-0">
            <thead className="bg-light">
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Subject</th>
                <th>Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentContacts.length > 0 ? (
                stats.recentContacts.map((contact) => (
                  <tr key={contact._id}>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.subject}</td>
                    <td>{formatDate(contact.createdAt)}</td>
                    <td>
                      <span className={`badge bg-${contact.isRead ? 'success' : 'danger'}`}>
                        {contact.isRead ? 'Read' : 'Unread'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center py-3">No recent contact requests found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
        <Card.Footer className="bg-white">
          <Link to="/contacts" className="text-decoration-none">View all contact requests</Link>
        </Card.Footer>
      </Card>
    </Container>
  );
};

export default Dashboard; 